import { SampleSubType } from '@resistapp/common/sampling-point-types';
import { AdminArea, FullSamplesByUID, RawSamplingPoint, Sample, SampleColumns } from '@resistapp/common/types';
import _, { isNil, uniq } from 'lodash';
import { flattenSamplesByUID } from './utils';

export function getGeneralSamplingPointSubtype(subtype: SampleSubType | undefined) {
  if (isNil(subtype)) {
    return undefined;
  }
  switch (true) {
    case subtype.includes('HOSPITAL_'):
      return 'hospital';
    case subtype.includes('URBAN_RUNOFF'):
      return 'runoff';
    case subtype.includes('URBAN_'):
      return 'urban';
    case subtype.includes('SEPTIC_'):
      return 'septic';
    default:
      return undefined;
  }
}

export function getAdminAreaKey(area: AdminArea) {
  const prefix = `${area.level}---${area.name}---`;
  return area.boundaries ? prefix + JSON.stringify(area.boundaries) : prefix;
}

export function getSamplingPointPoolingKey(sample: Sample) {
  return `${sample.samplingPoint.name}---${sample.samplingPoint.type}---${sample.samplingPoint.subtype}`;
}

export function getOrgUniqueSamplingPointKey(samplingPoint: RawSamplingPoint): string {
  return `${samplingPoint.name} --XYZ-- ${samplingPoint.type} --ZYX-- ${samplingPoint.subtype} --ABC-- ${samplingPoint.country} --PQR-- ${samplingPoint.region} --LMN-- ${samplingPoint.city} --STU-- ${samplingPoint.lat} --VWX-- ${samplingPoint.lon}`;
}

export function getBioRepGroupKey(sample: Pick<Sample, 'samplingPointId' | 'number' | 'time'>) {
  // Time is used instead of the sample number as the sample number is no longer quaranteed to be unique across projects
  // Note: Either all project samples are required to have time, or non of them can have time (and sample number is guaranteed to be unique)
  // Sampling point id here is essentially the same as getGloballyUniqueSamplingPointKey
  return `${sample.samplingPointId}-${isNil(sample.time) ? `${sample.number}` : sample.time.toISOString()}`; // UTC ok
}

export function hasConsistentBioGroupNumbers(samplesByBioKey: FullSamplesByUID): boolean {
  const flatSamples = flattenSamplesByUID(samplesByBioKey);
  const hasBioRepGroupWithMultipleSampleNumbers = !!_.chain(flatSamples)
    .groupBy(s => getBioRepGroupKey(s))
    .values()
    .map(bioRepSamples => uniq(bioRepSamples.map(s => s.number)))
    .filter(uniqBioRepSampleNums => uniqBioRepSampleNums.length > 1)
    .value().length;
  return !hasBioRepGroupWithMultipleSampleNumbers && !hasDuplicateBioNumbers(samplesByBioKey);
}

export function hasDuplicateBioNumbers(samplesByBioKey: FullSamplesByUID): boolean {
  const flatSamples = flattenSamplesByUID(samplesByBioKey);
  const sampleNumBioRepCombos = _.chain(flatSamples)
    .map(s => getSimpleBioNumber(s))
    .uniq()
    .value();
  return sampleNumBioRepCombos.length !== flatSamples.length;
}

export function getRobustBioNumber(
  sample: Pick<Sample, 'number' | 'bioRep' | 'id'>,
  hasDuplicateBioNums: boolean,
): string {
  const bioNumber = getSimpleBioNumber(sample);
  return hasDuplicateBioNums ? `${sample.id}-${bioNumber}` : bioNumber;
}

export function getSimpleBioNumber(sample: Pick<SampleColumns, 'number' | 'bioRep'>) {
  // "Simple" because sample number and bioRep combo may have duplicates within projects
  // (and because 1A 1B numbering may not represent the whole bioRep group, see above getBioRepGroupKey)
  // New lab-workbooks may not use bioRep letters; ensure we don't render "null".
  const num = sample.number == null ? '' : String(sample.number);
  const rep = sample.bioRep ?? '';
  return `${num}${rep}`;
}
